import { useBizQuery, useBizMutation } from './useBizQuery';
import * as whiteboardService from '../Services/whiteboard';

import { WhiteboardUpdateRequest, WhiteboardSnapshotRequest, WhiteboardFile } from '../Types/whiteboard';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

const QUERYKEY_WHITEBOARD = 'WHITEBOARD';
const QUERYKEY_WHITEBOARD_JOIN = 'WHITEBOARD_JOIN';
const QUERYKEY_WHITEBOARD_SNAPSHOT = 'WHITEBOARD_SNAPSHOT';

export const useGetWhiteboard = (objectType: number, objectId: number) => {
	const { data, statusState } = useBizQuery([QUERYKEY_WHITEBOARD, objectType.toString(), objectId.toString()], () =>
		whiteboardService.getWhiteboard(objectType, objectId)
	);
	return { data, statusState };
};

// New MemoryCache-backed join hook - only fetches after SignalR join
export const useJoinWhiteboard = (objectType: number, objectId: number, hasJoinedSignalR: boolean = false) => {
	const queryClient = useQueryClient();

	const { data, statusState } = useBizQuery(
		[QUERYKEY_WHITEBOARD_JOIN, objectType.toString(), objectId.toString()],
		() => whiteboardService.joinWhiteboard(objectType, objectId),
		hasJoinedSignalR, // Only fetch after SignalR join confirmation
		undefined,
		'always' // Always refetch on mount to ensure up-to-date data after join
	);

	const resetQuery = useCallback(() => {
		console.log('Resetting whiteboard join query cache');
		queryClient.resetQueries({
			queryKey: [QUERYKEY_WHITEBOARD_JOIN],
		});
	}, [queryClient]);

	const removeQuery = useCallback(() => {
		console.log('Removing whiteboard join query cache');
		queryClient.removeQueries({
			queryKey: [QUERYKEY_WHITEBOARD_JOIN],
		});
	}, [queryClient]);

	return { data, statusState, resetQuery, removeQuery };
};

export const useSaveWhiteboard = () => {
	const wrapperFn = (params: { whiteboard: WhiteboardUpdateRequest }) => {
		console.log('Saving whiteboard to backend', params.whiteboard);
		return whiteboardService.saveWhiteboard(params.whiteboard);
	};
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_WHITEBOARD], [QUERYKEY_WHITEBOARD_JOIN]],
	});

	return {
		saveWhiteboard: (whiteboard: WhiteboardUpdateRequest) => mutate({ whiteboard }),
		result: data,
		statusState,
	};
};

export const useCreateSnapshot = () => {
	const wrapperFn = (params: { request: WhiteboardSnapshotRequest }) => {
		console.log('Creating backend snapshot');
		return whiteboardService.createSnapshot(params.request);
	};
	const { mutate, statusState, data } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_WHITEBOARD_SNAPSHOT]],
	});

	return {
		createSnapshot: (request: WhiteboardSnapshotRequest) => mutate({ request }),
		result: data,
		statusState,
	};
};

export const useSaveWhiteboardFile = () => {
	const wrapperFn = (params: { whiteboardId: number; file: WhiteboardFile }) => {
		console.log('Saving whiteboard file to backend', params.file);
		return whiteboardService.saveWhiteboardFile(params.whiteboardId, params.file);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn);

	return {
		saveWhiteboardFile: (whiteboardId: number, file: WhiteboardFile) => mutate({ whiteboardId, file }),
		statusState,
	};
};

export const useDeleteWhiteboard = () => {
	const wrapperFn = (params: { whiteboardId: number }) => {
		return whiteboardService.deleteWhiteboard(params.whiteboardId);
	};
	const { mutate, statusState } = useBizMutation(wrapperFn, {
		getQueryKeyForInvalidation: () => [[QUERYKEY_WHITEBOARD], [QUERYKEY_WHITEBOARD_JOIN]],
	});

	return {
		deleteWhiteboard: (whiteboardId: number) => mutate({ whiteboardId }),
		statusState,
	};
};

export const useGetSnapshotHistory = (whiteboardId: number, maxResults = 10) => {
	const { data, statusState } = useBizQuery(
		[QUERYKEY_WHITEBOARD_SNAPSHOT, whiteboardId.toString(), maxResults.toString()],
		() => whiteboardService.getSnapshotHistory(whiteboardId, maxResults)
	);
	return { data, statusState };
};
